import { useEffect } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Play, Clock, CheckCircle, XCircle, Ban, Terminal } from 'lucide-react'

interface User {
  id: number
  first_name: string
  last_name: string
  email: string
}

interface Execution {
  id: number
  task_name: string
  parameters: Record<string, string>
  status: string
  exit_code: number | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  duration_display: string | null
  user: User
}

interface TaskConfig {
  description: string
  category: string
  destructive: boolean
  confirmation_message?: string
  required_env_vars: string[]
  parameters: Array<{
    name: string
    type: string
    required: boolean
    description: string
  }>
}

interface TasksByCategory {
  [category: string]: {
    [taskName: string]: TaskConfig
  }
}

interface AdminRakeTasksIndexProps {
  executions: Execution[]
  tasks_by_category: TasksByCategory
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Index({ executions, tasks_by_category }: AdminRakeTasksIndexProps) {
  const { t } = useTranslation('admin')

  // Auto-refresh every 5 seconds if there are running tasks
  useEffect(() => {
    const hasRunning = executions.some(e => e.status === 'running' || e.status === 'pending')
    if (hasRunning) {
      const interval = setInterval(() => {
        router.reload({ only: ['executions'] })
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [executions])

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: "success" | "warning" | "info" | "destructive" | "secondary"; icon: typeof CheckCircle }> = {
      'pending': { variant: 'secondary', icon: Clock },
      'running': { variant: 'info', icon: Terminal },
      'completed': { variant: 'success', icon: CheckCircle },
      'failed': { variant: 'destructive', icon: XCircle },
      'cancelled': { variant: 'warning', icon: Ban },
    }

    const config = statusConfig[status] || { variant: 'secondary' as const, icon: Clock }
    const Icon = config.icon

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    )
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('rakeTasks.title') }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground">{t('rakeTasks.title')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('rakeTasks.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Available Tasks */}
                <div className="lg:col-span-1 space-y-4">
                  <h2 className="text-lg font-semibold text-foreground">{t('rakeTasks.availableTasks')}</h2>

                  {Object.entries(tasks_by_category).map(([category, tasks]) => (
                    <Card key={category} className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">{category}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-1">
                        {Object.entries(tasks).map(([taskName, config]) => (
                          <div key={taskName} className="flex items-center justify-between py-1">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-mono truncate" title={taskName}>
                                {taskName}
                              </p>
                              <p className="text-xs text-muted-foreground truncate" title={config.description}>
                                {config.description}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              {config.destructive && (
                                <Badge variant="destructive" className="text-xs">
                                  {t('rakeTasks.destructive')}
                                </Badge>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => router.visit(`/admin/rake_tasks/new?task_name=${encodeURIComponent(taskName)}`)}
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Execution History */}
                <div className="lg:col-span-2">
                  <h2 className="text-lg font-semibold text-foreground mb-4">{t('rakeTasks.executionHistory')}</h2>

                  {executions.length > 0 ? (
                    <div className="space-y-3">
                      {executions.map((execution) => (
                        <Card key={execution.id} className="shadow-none">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Link
                                    href={`/admin/rake_tasks/${execution.id}`}
                                    className="font-mono text-sm font-medium hover:underline"
                                  >
                                    {execution.task_name}
                                  </Link>
                                  {getStatusBadge(execution.status)}
                                </div>
                                <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground">
                                  <span>{t('rakeTasks.by')} {execution.user.first_name} {execution.user.last_name}</span>
                                  <span>{formatDateTime(execution.created_at)}</span>
                                  {execution.duration_display && (
                                    <span>{t('rakeTasks.duration')}: {execution.duration_display}</span>
                                  )}
                                  {execution.exit_code !== null && (
                                    <span>{t('rakeTasks.exitCode')}: {execution.exit_code}</span>
                                  )}
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                asChild
                              >
                                <Link href={`/admin/rake_tasks/${execution.id}`}>
                                  {t('rakeTasks.viewDetails')}
                                </Link>
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="shadow-none">
                      <CardContent className="py-12 text-center">
                        <Terminal className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">{t('rakeTasks.noExecutions')}</p>
                        <p className="text-sm text-muted-foreground mt-1">{t('rakeTasks.selectTaskToRun')}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Live Update Notice */}
                  {executions.some(e => e.status === 'running' || e.status === 'pending') && (
                    <Card className="shadow-none mt-4 border-blue-500/20 bg-blue-500/5">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></div>
                          {t('rakeTasks.autoRefreshing')}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
